import { useEffect, useState } from "react";
import { Divider, Grid, IconButton, Tooltip } from "@mui/material";

import Box from "../Box";
import Modal from "../Modal";
import Radio from "../Radio";
import Button from "../Button";
import FormField from "../FormField";
import TitleValue from "../TitleValue";
import Typography from "../Typography";
import CustomIcon from "../CustomIcon";
import Autocomplete from "../Autocomplete";
import Helpers from "../../commons/helpers";
import useStrings from "../../hooks/useStrings";
import PhoneNumberInput from "../PhoneNumberInput";
import AddressService from "../../services/common/address.service";
import UserContactService from "../../services/identity/userContact.service";

import { IsTrue, } from "../../constants/enums";
import { IAddress, ICodename, ICountries } from "../../commons/interfaces";



interface IUserContact extends IAddress {
    id: string;
    name: number;
    type: number;
    default: number;
    userId: string;
    addressId: string;
    faxNumber: string;
    phoneNumber: string;
}

interface IModelUserContact {
    id?: string;
    default?: number;
    faxNumber?: string;
    phoneNumber?: string;

    addressId?: string;

    postalCode: string;
    country: string;
    cityName?: string;
    provinceName?: string;
    districtName?: string;

    addressLine: string;
    addressLine2: string;
}

type IError = { [k in keyof IModelUserContact]?: string };

interface IUserContactProps {
    idHref: string;
    idUserProfile?: string;

    onError: (msg: string) => void;
    onSuccess: (msg: string) => void;
    onLoading: (value: boolean) => void;
}

export const getFullAddressLine = (address: IAddress) => {
    let strAddress: string[] = [];
    if (address?.addressLine || address?.addressLine2) {
        if (address.addressLine) {
            strAddress.push(address?.addressLine);
        } else if (address?.addressLine2) {
            strAddress.push(address?.addressLine2);
        }
    }

    if (address?.districtName) {
        strAddress.push(address?.districtName);
    }

    if (address?.provinceName) {
        strAddress.push(address?.provinceName);
    }

    if (address?.cityName) {
        strAddress.push(address?.cityName);
    }

    if (address?.countryName) {
        strAddress.push(address?.countryName);
    }

    return strAddress.join(", ");
};

const UserContact = ({
    idHref,
    idUserProfile,

    onError,
    onSuccess,
    onLoading,
}: IUserContactProps) => {
    const strings = useStrings();
    const [modelError, setModelError] = useState<IError>({} as IError);
    const [model, setModel] = useState<IModelUserContact>({} as IModelUserContact);

    const [dataCountries, setDataCountries] = useState<ICodename[]>([]);
    const [dataUserContacts, setDataUserContacts] = useState<IUserContact[]>([]);

    const [openModalCreateEdit, setOpenModalCreateEdit] = useState<boolean>(false);
    const [openModalListContactInfo, setOpenModalListContactInfo] = useState<boolean>(false);

    useEffect(() => {
        if (!Helpers.isNullOrEmpty(idUserProfile)) {
            getAllUserContact();
            getAllDataCountries();
        }
    }, [idUserProfile]);

    const getAllUserContact = async () => {
        try {
            onLoading(true);

            const resultUserContacts: any = await UserContactService.getAllUserContact();
            if (resultUserContacts.length > 0) {
                let itemDefault: IUserContact;
                let newDataUserContacts: IUserContact[] = [];

                const queryId: string[] = resultUserContacts.map((item: any) => item?.addressId);
                const resultAddresses: IAddress[] = await AddressService.getAddressByIds({ ids: queryId });

                resultUserContacts.forEach((item: any) => {
                    const itemAddress = resultAddresses.find(el => (el.id === item?.addressId));

                    const newItemContact: IUserContact = {
                        id: item?.id,
                        name: item?.name,
                        type: item?.type,
                        default: item?.default,
                        userId: item?.userId,
                        addressId: item?.addressId,
                        faxNumber: item?.faxNumber,
                        phoneNumber: item?.phoneNumber,
                        status: item?.status,
                        createTime: item?.createTime,
                        createUser: item?.createUser,
                        updateTime: item?.updateTime,
                        updateUser: item?.updateUser,
                        postalCode: itemAddress?.postalCode,
                        lattitude: itemAddress?.lattitude,
                        longitude: itemAddress?.longitude,
                        addressLine: itemAddress?.addressLine,
                        addressLine2: itemAddress?.addressLine2,
                        city: itemAddress?.city,
                        cityName: itemAddress?.cityName,
                        cityResponseName: itemAddress?.cityResponseName,
                        country: itemAddress?.country,
                        countryCode: itemAddress?.countryCode,
                        countryName: itemAddress?.countryName,
                        district: itemAddress?.district,
                        districtName: itemAddress?.districtName,
                        districtResponseName: itemAddress?.districtResponseName,
                        province: itemAddress?.province,
                        provinceCode: itemAddress?.provinceCode,
                        provinceName: itemAddress?.provinceName,
                        provinceResponseName: itemAddress?.provinceResponseName,
                        ward: itemAddress?.ward,
                        wardName: itemAddress?.wardName,
                        wardResponseName: itemAddress?.wardResponseName,
                        organizationId: itemAddress?.organizationId,
                        tenantCode: itemAddress?.tenantCode,
                    };

                    if ((item?.default || IsTrue.False) === IsTrue.True) {
                        itemDefault = newItemContact;
                    } else {
                        newDataUserContacts.push(newItemContact);
                    }
                });

                setDataUserContacts([itemDefault, ...newDataUserContacts || []]);
            }

            onLoading(false);
        } catch (error) {
            const e = Helpers.renderExceptionError(error);
            onError(e);
        }
    };

    const getAllDataCountries = async () => {
        try {

            const result: ICountries[] = await AddressService.getCountries({ type: 1 });
            const newData: ICodename[] = result.map((item: any) => {
                return {
                    code: item?.id,
                    name: item?.name,
                };
            });

            setDataCountries(newData);
        } catch (error) { }
    };

    // 
    const onChangeValue = (newValue: any, key: string, isRequired?: boolean) => {
        let newModel: any = { ...model };
        let newModelError: any = { ...modelError };

        newModel[key] = newValue;

        if (isRequired === true) {
            newModelError[key] = "";
            setModelError(newModelError);
        }

        setModel(newModel);
    }

    const checkedValidate = () => {
        let isValid = true;
        let newDataError: any = { ...modelError };

        if (Helpers.isNullOrEmpty((model as any)["country"])) {
            isValid = false;
            newDataError["country"] = strings.Common.REQUIRED;
        }
        if (Helpers.isNullOrEmpty((model as any)["faxNumber"])) {
            isValid = false;
            newDataError["faxNumber"] = strings.Common.REQUIRED;
        }
        // if (Helpers.isNullOrEmpty((model as any)["provinceName"])) {
        //     isValid = false;
        //     newDataError["provinceName"] = strings.Common.REQUIRED;
        // }
        // if (Helpers.isNullOrEmpty((model as any)["postalCode"])) {
        //     isValid = false;
        //     newDataError["postalCode"] = strings.Common.REQUIRED;
        // }

        // if (Helpers.isNullOrEmpty((model as any)["addressLine"])) {
        //     isValid = false;
        //     newDataError["addressLine"] = strings.Common.REQUIRED;
        // }

        if (Helpers.isNullOrEmpty((model as any)["phoneNumber"])) {
            isValid = false;
            newDataError["phoneNumber"] = strings.Common.REQUIRED;
        } else {
            if ((model as any)["phoneNumber"]?.length < 8) {
                isValid = false;
                newDataError["phoneNumber"] = strings.Common.WRONG_PHONE_NUMBER;
            }
        }

        if (!isValid) {
            setModelError(newDataError);
        }

        return isValid;
    };

    const onCloseModalCreateEdit = () => {
        setOpenModalCreateEdit(false);
        setOpenModalListContactInfo(true);

        setModelError({});
        setModel({} as IModelUserContact);
    };

    const onSetDataModalEdit = (item: IUserContact) => {
        const newModel: IModelUserContact = {
            id: item?.id,
            default: item?.default,
            faxNumber: item?.faxNumber,
            phoneNumber: item?.phoneNumber,

            addressId: item?.addressId,
            addressLine: item?.addressLine,
            addressLine2: item?.addressLine2,
            country: item?.country,
            cityName: item?.cityName,
            postalCode: item?.postalCode,
            provinceName: item?.provinceName,
            districtName: item?.districtName,
        };

        setModel(newModel);

        setOpenModalListContactInfo(false);
        setOpenModalCreateEdit(true);
    }

    const onUpdateContactDefault = (item: IUserContact) => {
        if ((item?.default || IsTrue.False) !== IsTrue.True) {
            Helpers.showConfirmAlert(strings.Profile.CONFIRM_UPDATE_CONTACT_DEFAULT, async () => {
                try {
                    onLoading(true);

                    const contactPrams = {
                        id: item?.id,
                        default: IsTrue.True,
                        addressId: item?.addressId,
                        faxNumber: item?.faxNumber || "",
                        phoneNumber: item?.phoneNumber || "",
                    };

                    const resultContact = await UserContactService.updateContact(contactPrams);

                    if (resultContact.statusCode === 200) {
                        await getAllUserContact();

                        await onSuccess(strings.Profile.UPDATE_SUCCESS);
                    }

                    onLoading(false);
                } catch (error) {
                    const e = Helpers.renderExceptionError(error);
                    onError(e);
                }
            });
        }
    }

    const onDeleteContact = (idItem: string) => {
        Helpers.showConfirmAlert(strings.Profile.CONFIRM_DELETE_CONTACT, async () => {
            try {
                onLoading(true);

                const resultContact = await UserContactService.deleteContact(idItem);

                if (resultContact.statusCode === 200) {
                    await getAllUserContact();
                    await onSuccess(strings.Profile.DELETE_USER_CONTACT_SUCCESS);
                }

                onLoading(false);
            } catch (error) {
                const e = Helpers.renderExceptionError(error);
                onError(e);
            }
        });
    }

    const onCreateEditUserContact = async () => {
        if (!checkedValidate()) return;

        try {
            onLoading(true);

            const addressParams: any = {
                organizationId: 0,
                addressLine: model.addressLine,
                addressLine2: model.addressLine2,
                country: model.country,
                cityName: model.cityName,
                provinceName: model.provinceName,
                districtName: model.districtName,
                postalCode: model.postalCode,
            };


            let resultAddresses: any = {};
            if (Helpers.isNullOrEmpty(model.addressId)) {
                resultAddresses = await AddressService.createAddress(addressParams);
            } else {
                resultAddresses = await AddressService.updateAddress({
                    ...addressParams,
                    id: model.addressId,
                });
            }

            if (resultAddresses.statusCode === 200) {
                let resultContact: any = {};

                const contactPrams = {
                    addressId: resultAddresses.result?.id,
                    faxNumber: model.faxNumber || "",
                    phoneNumber: model.phoneNumber || "",
                    default: model.default || IsTrue.False,
                };

                if (Helpers.isNullOrEmpty(model.id)) {
                    resultContact = await UserContactService.createContact(contactPrams);
                } else {
                    resultContact = await UserContactService.updateContact({
                        ...contactPrams,
                        id: model.id,
                    });
                }

                if (resultContact.statusCode === 200) {
                    await getAllUserContact();

                    setOpenModalCreateEdit(false);

                    setModelError({});
                    setModel({} as IModelUserContact);
                    if (dataUserContacts.length > 0) {
                        setOpenModalListContactInfo(true);
                    }

                    onLoading(false);

                    const msg = Helpers.isNullOrEmpty(model.id)
                        ? strings.Profile.CREATE_USER_CONTACT_SUCCESS
                        : strings.Profile.UPDATE_USER_CONTACT_SUCCESS
                    onSuccess(msg);
                }
            }

            onLoading(false);
        } catch (error) {
            const e = Helpers.renderExceptionError(error);
            onError(e);
        }
    };

    const renderModalCreteEditContactInfo = () => (
        <Modal
            fullWidth
            maxWidth={"md"}
            hasActionButton
            visible={openModalCreateEdit}
            onClose={onCloseModalCreateEdit}
            onAction={onCreateEditUserContact}
            title={model.id ? strings.Profile.UPDATE_USER_CONTACT : strings.Profile.CREATE_USER_CONTACT}
        >
            <Grid container spacing={2} p={2}>
                <Grid item xs={12} md={6}>
                    <PhoneNumberInput
                        required
                        country={"vn"}
                        defaultValue={model.phoneNumber}
                        errorMessage={modelError.phoneNumber}
                        onChangeValue={(value) => {
                            onChangeValue(value, "phoneNumber", true)
                        }}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <FormField
                        required
                        type="number"
                        maxLength={15}
                        value={model.faxNumber || ""}
                        errorMessage={modelError.faxNumber}
                        label={strings.Organization.FAX_NUMBER}
                        placeholder={strings.Organization.ENTER_FAX_NUMBER}
                        onChangeValue={(value) => {
                            onChangeValue(value, "faxNumber", true)
                        }}
                    />
                </Grid>

                <Grid item xs={12} md={6}>
                    <Autocomplete
                        required
                        data={dataCountries}
                        defaultValue={model.country}
                        label={strings.Profile.COUNTRY}
                        errorMessage={modelError.country}
                        placeholder={strings.Profile.SELECT_COUNTRY}
                        onChange={(value: any) => {
                            onChangeValue(value, "country", true);
                        }}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <FormField
                        maxLength={255}
                        defaultValue={model.provinceName}
                        errorMessage={modelError.provinceName}
                        label={strings.Profile.PROVINCE_STATE_NAME}
                        placeholder={strings.Profile.ENTER_PROVINCE_STATE_NAME}
                        onBlur={(value: string) => {
                            onChangeValue(value, "provinceName", true);
                        }}
                    />
                </Grid>

                <Grid item xs={12} md={6}>
                    <FormField
                        maxLength={255}
                        defaultValue={model.cityName}
                        label={strings.Profile.CITY_NAME}
                        placeholder={strings.Profile.ENTER_CITY_NAME}
                        onBlur={(value: string) => {
                            onChangeValue(value, "cityName");
                        }}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <FormField
                        maxLength={255}
                        defaultValue={model.districtName}
                        label={strings.Profile.DISTRICT_NAME}
                        placeholder={strings.Profile.ENTER_DISTRICT_NAME}
                        onBlur={(value: string) => {
                            onChangeValue(value, "districtName");
                        }}
                    />
                </Grid>

                <Grid item xs={12} md={6}>
                    <FormField
                        type="number"
                        maxLength={6}
                        defaultValue={model.postalCode}
                        errorMessage={modelError.postalCode}
                        label={strings.Profile.POSTAL_CODE}
                        placeholder={strings.Profile.ENTER_POSTAL_CODE}
                        onBlur={(value: string) => {
                            onChangeValue(value, "postalCode", true);
                        }}
                    />
                </Grid>

                <Grid item xs={12}>
                    <FormField
                        // required
                        maxLength={255}
                        defaultValue={model.addressLine}
                        label={strings.Profile.ADDRESS_LINE}
                        placeholder={strings.Profile.ENTER_ADDRESS_LINE}
                        errorMessage={modelError.addressLine}
                        onBlur={(value: string) => {
                            onChangeValue(value, "addressLine");
                        }}
                    />
                </Grid>

                <Grid item xs={12}>
                    <FormField
                        maxLength={255}
                        defaultValue={model.addressLine2}
                        label={strings.Profile.ADDRESS_LINE2}
                        placeholder={strings.Profile.ENTER_ADDRESS_LINE2}
                        onBlur={(value: string) => {
                            onChangeValue(value, "addressLine2");
                        }}
                    />
                </Grid>

            </Grid>
        </Modal>
    );

    const renderTitleValue = (title: string, value?: any) => (
        <Typography variant="button">
            <Typography variant="button" fontWeight="bold">{title}:</Typography>
            &emsp;
            <Typography variant="button">{value}</Typography>
        </Typography>
    )

    const renderModalListContactInfo = () => (
        <Modal
            fullWidth
            maxWidth={"sm"}
            hasActionButton
            visible={openModalListContactInfo}
            buttonAction={strings.Common.ADD_NEW}
            title={strings.Profile.LIST_USER_CONTACT}
            onClose={() => { setOpenModalListContactInfo(false) }}
            onAction={() => {
                setOpenModalListContactInfo(false);
                setOpenModalCreateEdit(true);
            }}
        >
            <Grid container spacing={2} p={2}>
                {(dataUserContacts.length === 0)
                    ? <Grid item xs={12}>
                        <Box textAlign="center">
                            <Typography variant="button">{strings.Common.NO_DATA}</Typography>
                        </Box>
                    </Grid>
                    : dataUserContacts.map((item, index) => {
                        return (
                            <Grid key={`uc_${index}`} item xs={12}>
                                <Box mb={1.5} display="flex" alignItems="flex-start">
                                    <Radio
                                        checked={(item?.default || IsTrue.False) === IsTrue.True}
                                        readOnly={(item?.default || IsTrue.False) === IsTrue.True}
                                        onChange={() => { onUpdateContactDefault(item) }}
                                    />
                                    <Grid container spacing={0}>
                                        <Grid item xs={12} sm={6}>
                                            {renderTitleValue(strings.Organization.PHONE_NUMBER, item?.phoneNumber ? `+${item?.phoneNumber}` : "")}
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            {renderTitleValue(strings.Organization.FAX_NUMBER, item?.faxNumber)}
                                        </Grid>
                                        <Grid item xs={12} sm={12} display="flex">
                                            {renderTitleValue(strings.Profile.ADDRESS_LINE, getFullAddressLine(item))}
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            {
                                                ((item?.default || IsTrue.False) === IsTrue.True) &&
                                                <Typography sx={{ fontStyle: "italic" }} fontWeight="bold" color="success" variant="button">
                                                    {strings.Common.DEFAULT}
                                                </Typography>
                                            }
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <Box display="flex" justifyContent="end">
                                                <Tooltip title={strings.Common.EDIT}>
                                                    <IconButton onClick={() => { onSetDataModalEdit(item) }} >
                                                        <CustomIcon iconName="edit" />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title={strings.Common.DELETE}>
                                                    <IconButton onClick={() => { item?.id && onDeleteContact(item?.id) }} >
                                                        <CustomIcon iconName="delete" />
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                        </Grid>
                                    </Grid>
                                </Box>
                                <Divider />
                            </Grid>
                        );
                    })}
            </Grid>
        </Modal>
    );

    return (
        <Box key={`uc`} id={idHref} p={2}>
            <Box display="flex">
                <Typography variant="h5">{strings.Profile.USER_CONTACT}</Typography>
            </Box>
            <Grid container spacing={2} p={2}>
                <Grid item xs={12} md={6}>
                    <TitleValue
                        title={strings.Organization.PHONE_NUMBER}
                        value={
                            dataUserContacts[0]?.phoneNumber ? `+${dataUserContacts[0]?.phoneNumber}` : ""
                        }
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <TitleValue
                        title={strings.Organization.FAX_NUMBER}
                        value={dataUserContacts[0]?.faxNumber}
                    />
                </Grid>
                <Grid item xs={12}>
                    <TitleValue
                        isFullRow
                        title={strings.Profile.ADDRESS_LINE}
                        value={(dataUserContacts.length > 0) ? getFullAddressLine(dataUserContacts[0]) : ""}
                    />
                </Grid>
                <Grid item xs={12}>
                    <Box display="flex" style={{ float: "right" }}>
                        <Button onClick={() => { setOpenModalListContactInfo(true) }}>
                            {(dataUserContacts.length === 0) ? strings.Common.ADD_NEW : strings.Common.EDIT}
                        </Button>
                    </Box>
                </Grid>
            </Grid>
            {renderModalCreteEditContactInfo()}
            {renderModalListContactInfo()}
        </Box>
    );
};

export default UserContact;
